import { useEffect, useState, type ReactNode } from "react";
import { HelpVideo } from "../ui/HelpVideo";
import type { HelpVideoId } from "../../lib/helpVideos";
import { Button } from "../ui/Button";
import { PlayerSourceDialog } from "./PlayerSourceDialog";
import { fetchPlayerCostSources } from "../../lib/api";
import { Hint } from "../ui/Field";
import { Segmented } from "../ui/Panel";
import { Tag } from "../ui/Tag";
import { costSourceKind, costSourceName, COST_SOURCE_FORMULA, COST_SOURCE_MANUAL, rivalSource } from "../../lib/costSource";
import type { RivalDef } from "../../lib/fixtures";
import { useT } from "../../lib/i18n";
import { ApiTag } from "../apilens/ApiLens";

/**
 * 「客户端用哪套造价」一条：总造价页、萌宠星级表共用。
 *
 * 三套造价平级、都能发布（leo 2026-09-16）：公式 / 手填 / 某一列竞品。选哪套，发布时客户端就拿哪套的价，
 * 发布闸只校验选中的那套；切换不动任何一套的数值。它是版本里的字段（cfg_main_common.cost_source），随版本发布与回滚。
 *
 * 右侧「按玩家指定 · N」（leo 2026-09-17）：让几个测试号单独用另一套价，其余人仍用版本默认，一个版本里同时测公式和竞品。
 * 指定不跟版本走，所以草稿与已发布版本都能点（不看 editable）。
 */
export function CostSourceBar({
  value, rivals, editable, onChange, onAddRival, failed, count, unit, noteInHint = false, api, video,
}: {
  /** 标签旁的说明视频（lib/helpVideos.ts 的 id）；总造价页给，别的页不给 */
  video?: HelpVideoId;
  /** 接口视角的登记 id（src/api-lens） */
  api?: string;
  value: string;
  rivals: RivalDef[];
  editable: boolean;
  onChange: (v: string) => void;
  onAddRival?: () => void;
  /** 按在用的那套没通过的关 / 星数 */
  failed: number;
  count: number;
  /** 计数单位：关 / 颗星 */
  unit: "level" | "pettownStar";
  /** 「发布闸查什么」那句不单独占一段，并进问号（总造价页，2026-10-09 leo：页面太乱） */
  noteInHint?: boolean;
}) {
  const t = useT();
  const kind = costSourceKind(value);
  const [assigning, setAssigning] = useState(false);
  const [assigned, setAssigned] = useState<number | null>(null);
  useEffect(() => {
    let alive = true;
    fetchPlayerCostSources().then((d) => { if (alive) setAssigned(d.rows.length); }).catch(() => { /* 拉不到就不显示数字 */ });
    return () => { alive = false; };
  }, []);
  const options = [
    { value: COST_SOURCE_FORMULA, label: t("cost.src.formula") },
    { value: COST_SOURCE_MANUAL, label: t("cost.src.manual") },
    ...rivals.map((r) => ({ value: rivalSource(r.id), label: r.name })),
  ];
  const unitText = t(unit === "level" ? "u.level" : "u.pettownStar");
  const note = kind === "formula" ? t(unit === "pettownStar" ? "cost.src.note.formulaStar" : "cost.src.note.formula")
    : kind === "manual" ? t(unit === "pettownStar" ? "cost.src.note.manualStar" : "cost.src.note.manual")
    : kind === "rival" ? t(unit === "pettownStar" ? "cost.src.note.rivalStar" : "cost.src.note.rival")
    : t("cost.src.note.unset");
  return (
    <div data-api={api} className="mb-3 flex flex-wrap items-center gap-3 rounded-base border border-line-lighter bg-fill-lighter px-3 py-2.5">
      <span className="inline-flex items-center text-sm font-semibold">
        {t("cost.src.title")}<Hint text={noteInHint ? `${t("cost.src.hint")}${t("cost.src.hintNote", { note })}` : t("cost.src.hint")} />
        {video && <span className="ml-0.5 inline-flex"><HelpVideo id={video} variant="icon" /></span>}
      </span>
      {api && <ApiTag id={api} />}
      {editable
        ? <Segmented value={value} options={options} onChange={onChange} />
        : <Tag tone={kind ? "success" : "danger"}>{costSourceName(value, rivals, { formula: t("cost.src.formula"), manual: t("cost.src.manual") }) ?? t("cost.src.unset")}</Tag>}
      {/* 还没选来源时这句是红字提醒，照样单独显示 */}
      {(!noteInHint || !kind) && <span className={`text-xs ${kind ? "text-ink-secondary" : "text-danger"}`}>{note}</span>}
      <span className="ml-auto inline-flex items-center gap-2.5">
        <span className={`text-xs font-semibold ${failed ? "text-danger" : "text-success"}`}>
          {failed ? t("cost.src.failed", { n: failed, u: unitText }) : t("cost.src.allPass", { n: count, u: unitText })}
        </span>
        {editable && onAddRival && <Button size="sm" onClick={onAddRival}>{t("cost.addRival")}</Button>}
        <Button size="sm" onClick={() => setAssigning(true)} title={t("ps.entryHint")}>
          {assigned == null ? t("ps.entry") : t("ps.entryN", { n: assigned })}
        </Button>
      </span>
      {assigning && (
        <PlayerSourceDialog rivals={rivals} defaultSource={value} unit={unit} onClose={() => setAssigning(false)} onChanged={setAssigned} />
      )}
    </div>
  );
}

/** 表头上的「客户端在用」标 */
export function InUseTag({ on }: { on: boolean }) {
  const t = useT();
  return on ? <Tag tone="success">{t("cost.src.inUse")}</Tag> : null;
}

/**
 * 竞品列的表头：色点 + 名字 + ✎（改名）+ ×（删除）。三张表共用一份。
 *
 * 原来是一个灰色小「⋯」点开才有菜单，很多人找不到删除（leo 2026-09-17）。
 * 现在两个按钮直接露在表头上；× 不直接删，由调用方弹确认框（会一起删掉这一列已填的价）。
 * 「客户端在用」标放到名字下面一行，不再和名字抢宽度把名字挤成一个字。
 */
export function RivalHeader({
  rival, editable, inUse, onRename, onRemove,
}: { rival: RivalDef; editable: boolean; inUse: boolean; onRename: (r: RivalDef) => void; onRemove: (r: RivalDef) => void }) {
  const t = useT();
  const icon = "flex-none rounded-sm px-1 leading-none text-ink-placeholder";
  return (
    <span className="flex flex-col items-end gap-0.5">
      <span className="flex max-w-full items-center justify-end gap-1">
        <span className="h-1.5 w-1.5 flex-none rounded-full" style={{ background: rival.color }} />
        <span className="truncate" title={rival.name}>{rival.name}</span>
        {editable && (
          <>
            <button type="button" className={`${icon} hover:bg-fill-light hover:text-primary`} title={t("building.renameRival")}
              aria-label={t("building.renameRival")} onClick={(e) => { e.stopPropagation(); onRename(rival); }}>✎</button>
            <button type="button" className={`${icon} text-sm hover:bg-danger-50 hover:text-danger`} title={t("building.removeRival")}
              aria-label={t("building.removeRival")} onClick={(e) => { e.stopPropagation(); onRemove(rival); }}>×</button>
          </>
        )}
      </span>
      <InUseTag on={inUse} />
    </span>
  );
}

/** 校验列的一格：通过 / 未通过 + 悬停原因 */
export function CheckCell({ ok, lines }: { ok: boolean; lines: ReactNode[] }) {
  return (
    <span className={`inline-flex items-center gap-1 text-xs ${ok ? "text-success" : "text-danger"}`} title={lines.map(String).join("\n")}>
      <span className={`inline-block h-2.5 w-2.5 rounded-full ${ok ? "bg-success" : "bg-danger"}`} />
      {lines[0]}
    </span>
  );
}
